"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.SecureScaleOfflinePackage = exports.SecureScaleReplicaBundle = exports.SecureScaleReplicaPayload = exports.SecureScaleVersionSettings = exports.SecureScaleSettingsSource = exports.SecureScaleScaleSettings = exports.SecureScaleStoreSettings = exports.SecureScaleSharedSettings = void 0;
exports.canonicalSecureScaleJson = canonicalSecureScaleJson;
const securescale_commissioning_js_1 = require("./securescale-commissioning.js");
const zod_1 = require("zod");
const weight_js_1 = require("./weight.js");
const Id = zod_1.z.string().min(1).max(64);
const Revision = zod_1.z.number().int().safe().nonnegative();
const Percent = zod_1.z.string().regex(/^(?:100(?:\.0+)?|(?:\d|[1-9]\d)(?:\.\d+)?)$/);
/** BOS owns these shared keys. Learning-only parameters never belong here. */
exports.SecureScaleSharedSettings = zod_1.z
    .object({
    z: zod_1.z.number().int().min(1).max(10),
    pMin: Percent,
    cDiv: zod_1.z.number().int().min(1).max(20),
    noiseMg: zod_1.z.number().int().min(0).max(100_000),
    pSeed: Percent,
    presenceDivisions: zod_1.z.number().int().min(1).max(100),
    maxItemMg: zod_1.z.number().int().min(1000).max(100_000_000),
    pAuditMax: Percent,
    auditGain: Percent,
    stableTimeoutMs: zod_1.z.number().int().min(500).max(60_000),
    degradedMaxMinutes: zod_1.z.number().int().min(5).max(1440),
    presenceEpsilonGrams: zod_1.z.number().int().min(1).max(1000),
    /** Engineer-entered reaction threshold; absence means unconfigured pass-through. */
    minimumWeightChangeMg: zod_1.z.number().int().min(1).max(100_000_000).optional(),
})
    .strict();
exports.SecureScaleStoreSettings = exports.SecureScaleSharedSettings.pick({
    cDiv: true,
    noiseMg: true,
    presenceEpsilonGrams: true,
    minimumWeightChangeMg: true,
    pAuditMax: true,
    auditGain: true,
    stableTimeoutMs: true,
    degradedMaxMinutes: true,
})
    .partial()
    .strict();
exports.SecureScaleScaleSettings = exports.SecureScaleSharedSettings.pick({
    cDiv: true,
    noiseMg: true,
    presenceEpsilonGrams: true,
    minimumWeightChangeMg: true,
})
    .partial()
    .extend({ cashierKeyReader: zod_1.z.enum(['absent', 'installed']).optional() })
    .strict();
exports.SecureScaleSettingsSource = zod_1.z.enum(['default', 'tenant', 'store', 'scale']);
const SettingsKey = exports.SecureScaleSharedSettings.keyof();
const Sources = zod_1.z.record(SettingsKey, exports.SecureScaleSettingsSource);
exports.SecureScaleVersionSettings = zod_1.z
    .object({
    revision: Revision,
    values: exports.SecureScaleSharedSettings,
    /** Tenant base used by the central learner, distinct from store/scale checks. */
    tenantValues: exports.SecureScaleSharedSettings,
    sources: Sources,
    scaleOverrides: zod_1.z.record(Id, exports.SecureScaleScaleSettings),
})
    .strict()
    .superRefine((s, ctx) => {
    for (const key of ['z', 'pMin', 'pSeed', 'presenceDivisions', 'maxItemMg']) {
        if (s.values[key] !== s.tenantValues[key] ||
            !['tenant', 'default'].includes(s.sources[key] ?? ''))
            ctx.addIssue({
                code: zod_1.z.ZodIssueCode.custom,
                path: ['values', key],
                message: 'Central math setting must remain tenant-scoped',
            });
    }
    for (const key of exports.SecureScaleSharedSettings.keyof().options) {
        if (key === 'minimumWeightChangeMg' && s.values[key] === undefined)
            continue;
        if (s.sources[key] === undefined || s.sources[key] === 'scale') {
            ctx.addIssue({
                code: zod_1.z.ZodIssueCode.custom,
                path: ['sources', key],
                message: 'every base setting needs a non-scale effective source',
            });
        }
    }
});
const Domain = zod_1.z.object({ revision: Revision, count: Revision }).strict();
/** Store-scoped complete versions; a Link can distribute one to all its SCOs. */
exports.SecureScaleReplicaPayload = zod_1.z
    .object({
    schemaVersion: zod_1.z.literal(1),
    tenantId: Id,
    storeId: Id,
    versionId: Id,
    createdAt: zod_1.z.string().datetime(),
    settings: exports.SecureScaleVersionSettings,
    commissioning: securescale_commissioning_js_1.CommissioningState.optional(),
    domains: zod_1.z.object({ clusters: Domain, policies: Domain, risk: Domain }).strict(),
    clusters: zod_1.z.array(weight_js_1.WeightClusterRecord),
    policies: zod_1.z.array(weight_js_1.WeightPolicyRecord),
    risk: zod_1.z.array(weight_js_1.WeightRiskRecord),
})
    .strict()
    .superRefine((p, ctx) => {
    for (const domain of ['clusters', 'policies', 'risk']) {
        const rows = p[domain];
        if (rows.length !== p.domains[domain].count) {
            ctx.addIssue({
                code: zod_1.z.ZodIssueCode.custom,
                path: ['domains', domain, 'count'],
                message: 'complete domain count does not match rows',
            });
        }
        const ids = new Set();
        rows.forEach((row, i) => {
            const id = 'clusterId' in row ? row.clusterId : row.id;
            if (ids.has(id) || row.revision > p.domains[domain].revision) {
                ctx.addIssue({
                    code: zod_1.z.ZodIssueCode.custom,
                    path: [domain, i],
                    message: 'duplicate row or revision beyond complete snapshot',
                });
            }
            ids.add(id);
            if ('storeId' in row && row.storeId !== null && row.storeId !== p.storeId) {
                ctx.addIssue({
                    code: zod_1.z.ZodIssueCode.custom,
                    path: [domain, i, 'storeId'],
                    message: 'row belongs to another store',
                });
            }
        });
    }
});
exports.SecureScaleReplicaBundle = zod_1.z
    .object({
    payload: exports.SecureScaleReplicaPayload,
    /** Corruption detection only. Authentication is required separately. */
    sha256: zod_1.z.string().regex(/^[a-f0-9]{64}$/),
})
    .strict();
/** JSON with sorted object keys and original array order, shared hash input. */
function canonicalSecureScaleJson(value) {
    if (value === null || typeof value === 'string' || typeof value === 'boolean') {
        return JSON.stringify(value);
    }
    if (typeof value === 'number' && Number.isFinite(value))
        return value.toExponential(16);
    if (Array.isArray(value))
        return `[${value.map(canonicalSecureScaleJson).join(',')}]`;
    if (typeof value === 'object' && value !== null) {
        return `{${Object.entries(value)
            .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
            .map(([key, v]) => `${JSON.stringify(key)}:${canonicalSecureScaleJson(v)}`)
            .join(',')}}`;
    }
    throw new Error('replica hash input must be JSON');
}
/** Domain-separated offline provisioning; trusted keys come from authenticated enrollment. */
exports.SecureScaleOfflinePackage = zod_1.z
    .object({
    kind: zod_1.z.literal('securescale-offline-v1'),
    bundle: exports.SecureScaleReplicaBundle,
    kid: Id,
    issuedAt: zod_1.z.string().datetime(),
    expiresAt: zod_1.z.string().datetime(),
    signature: zod_1.z.string().min(1).max(128),
})
    .strict();
//# sourceMappingURL=securescale-version.js.map